import type { CSSProperties } from 'react'
import styles from './Odometer.module.css'

type RollDirection = 'up' | 'down'

/** `text` with every digit replaced by '0', to roll from when there's no previous value. */
export const zeroFrom = (text: string) => text.replace(/\d/g, '0')

// Every digit a reel passes through from `start` to `end`, wrapping 9 -> 0
// going up and 0 -> 9 going down.
const reelSequence = (start: number, end: number, direction: RollDirection) => {
  const digits = [String(start)]
  for (let d = start; d !== end;) {
    d = direction === 'up' ? (d + 1) % 10 : (d + 9) % 10
    digits.push(String(d))
  }
  return digits
}

type ReelStyle = CSSProperties & { '--start-y': string; '--end-y': string }

type OdometerProps = {
  value: string
  /** What to roll from, character by character; unchanged characters stay put. */
  from: string
  direction: RollDirection
  className?: string | undefined
}

/**
 * A digit-reel "odometer": each digit that differs
 * from `from` scrolls through every digit in between, the old one fading out
 * and the new one in, while the reel flashes green rolling up or red rolling
 * down. Every character sits in the same 1em slot, so they share a baseline.
 * Decorative: pair it with the plain value for assistive tech.
 */
export const Odometer = ({
  value,
  from,
  direction,
  className,
}: OdometerProps) => (
  // Keyed by both ends, so a new value rolls again rather than keeping the
  // finished animation.
  <span
    key={`${from}->${value}`}
    className={`${styles.odometer} ${className ?? ''}`}
    aria-hidden='true'
  >
    {value.split('').map((char, i) => {
      const fromChar = from[i]
      const isDigit = /\d/.test(char)
      const start =
        isDigit && fromChar !== undefined && /\d/.test(fromChar)
          ? fromChar
          : char
      const changed = start !== char
      const sequence = changed
        ? reelSequence(Number(start), Number(char), direction)
        : [char]
      // Rolling up, the reel runs bottom to top: new digit first, old last.
      if (direction === 'up') sequence.reverse()
      const travel = `${sequence.length - 1}em`
      const style: ReelStyle = {
        '--start-y': direction === 'up' ? `-${travel}` : '0',
        '--end-y': direction === 'up' ? '0' : `-${travel}`,
      }
      const oldIndex = direction === 'up' ? sequence.length - 1 : 0
      const newIndex = direction === 'up' ? 0 : sequence.length - 1

      return (
        <span key={i} className={styles.slot}>
          <span
            className={`${styles.reel} ${changed ? styles[direction] : ''}`}
            style={style}
          >
            {sequence.map((digit, j) => (
              <span
                key={j}
                className={`${styles.digit} ${
                  changed && j === oldIndex ? styles.old : ''
                } ${changed && j === newIndex ? styles.new : ''}`}
              >
                {digit}
              </span>
            ))}
          </span>
        </span>
      )
    })}
  </span>
)
